import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LinearProgress } from '@mui/material';
import { ErrorMessage } from '@hookform/error-message';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { postCreateFolder } from '../../../../api/foldersApi';
import Modal from '../../../../components/modules/Modal';
import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import { getFoldersThunk, updateTriggerFolderFetch } from '../../../../store/folders/foldersSlice';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import styles from './SidebarModals.module.scss';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import { useHotkeys } from 'react-hotkeys-hook';

type TAddFolderModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  parentFolderId?: string | null;
};

type FieldValues = {
  name: string;
};

const AddFolderModal: React.FC<TAddFolderModal> = ({ open, setOpen, parentFolderId }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FieldValues>();
  const dispatch = useAppDispatch();
  const { page, limit, sortOption } = useAppSelector((state) => state.folderStore);
  const [loading, setLoading] = useState(false);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const notification = useSnackbar();
  const switchModal = () => {
    setOpen(!open);
  };

  const onClose = () => {
    reset();
    switchModal();
  };

  const submitNewFolder = async (data: FieldValues) => {
    setLoading(true);
    try {
      await postCreateFolder({ name: data.name, ...(parentFolderId ? { parentFolderId } : {}) });
      dispatch(updateTriggerFolderFetch());
      onClose();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>{parentFolderId ? 'Create a new subfolder?' : 'Create a new folder?'}</div>
        <form onSubmit={handleSubmit(submitNewFolder)} className={styles.form}>
          <input
            {...register('name', {
              required: 'enter folder name',
            })}
            placeholder="Enter Folder Name"
            className={styles.field}
            type="text"
          />
          <ErrorMessage errors={errors} name="name" render={({ message }) => <div className="errorMessage">{message}</div>} />
          <ContainedButton className={styles.customBtn} disabled={loading} type="submit">
            <div ref={submitButtonRef}>Create</div>
          </ContainedButton>
          <OutlinedButton kind="default" onClick={onClose} type="button">
            Cancel
          </OutlinedButton>
        </form>
      </div>
    </Modal>
  );
};

export default AddFolderModal;
